// src/components/wallet/ROCKETFIRewards.js
import React, { useEffect, useState, useCallback } from 'react';
import BigNumber            from 'bignumber.js';
import { Button }           from 'primereact/button';
import { Progress }         from '@nextui-org/react';
import { formatCurrencyExtended, getTokenPrice } from '../../utils';
import RewardPackProfile    from './RewardPackProfile';

const ROCKETFIRewards = React.memo(
  ({
    account,
    contracts,
    imageReferences,
    tokenSelections,
    viewTotalDistributed = false,     // kept for API parity (not shown atm)
    enableClaiming       = false
  }) => {
    const [subscriptions,        setSubscriptions]   = useState([]);   // filled by <RewardPackProfile/>
    const [unclaimedUSD,         setUnclaimedUSD]    = useState(0);
    const [isLoadingRewards,     setIsLoading]       = useState(false);
    const [isClaiming,           setIsClaiming]      = useState(false);

    /* ───────────── central fetch helper ───────────── */
    const fetchUnclaimed = useCallback(async () => {
      if (!account || !contracts?.rp_rewardPacks || subscriptions.length === 0) {
        setUnclaimedUSD(0);
        return;
      }

      try {
        setIsLoading(true);
        let total = new BigNumber(0);

        for (const addr of subscriptions) {
          const tokenInfo = tokenSelections.find(
            t => t?.address?.toLowerCase() === addr?.toLowerCase()
          );
          if (!tokenInfo) continue;

          const unclaimedAmt = await contracts.rp_rewardPacks.methods
            .getTotalUnrealisedWithRealisedUndeposited(account, addr)
            .call();

          const px = await getTokenPrice(tokenSelections, tokenInfo.label);
          if (!px) continue;

          const usd = new BigNumber(unclaimedAmt)
            .times(px)
            .div(new BigNumber(10).pow(tokenInfo.decimals));

          total = total.plus(usd);
        }

        setUnclaimedUSD(total.toNumber());
      } catch (err) {
        console.error('Error calculating RocketFi unclaimed rewards:', err);
        setUnclaimedUSD(0);
      } finally {
        setIsLoading(false);
      }
    }, [account, contracts?.rp_rewardPacks, subscriptions, tokenSelections]);

    /* initial fetch + whenever deps change */
    useEffect(() => { fetchUnclaimed(); }, [fetchUnclaimed]);

    /* background refresh every 60 s */
    useEffect(() => {
      const id = setInterval(fetchUnclaimed, 60_000);
      return () => clearInterval(id);
    }, [fetchUnclaimed]);

    /* ───────────── claim handler ───────────── */
    const handleClaim = async () => {
      if (!contracts?.rp_rewardPacks || isClaiming) return;
      setIsClaiming(true);

      try {
        const tx = contracts.rp_rewardPacks.methods
          .claimAllCurrentDividends()
          .send({ from: account });

        tx.on('transactionHash', h =>
          console.log(`Tx: ${h}  (https://bscscan.com/tx/${h})`)
        );

        await tx;
        await fetchUnclaimed();            // self-refresh
      } catch (err) {
        console.error('Reward-Pack claim error:', err);
      } finally {
        setIsClaiming(false);
      }
    };

    /* ───────────── UI pieces ───────────── */
    const pendingBlock = isLoadingRewards
      ? <div>Loading Rewards…</div>
      : <div>Pending:&nbsp;<strong>{formatCurrencyExtended(unclaimedUSD)}</strong></div>;

    return (
      <div
        style={{
          display        : 'flex',
          justifyContent : 'flex-start',
          alignItems     : 'center',
          borderRadius   : 10,
          background     : 'rgba(50,100,255,0.05)',
          padding        : '5px 15px',
          fontSize       : 11
        }}
      >
        <div>
          {/* Header */}
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', marginBottom:8 }}>
            <div style={{ fontSize:12 }}><b>Reward Pack</b></div>

            <RewardPackProfile
              holderAddress   = {account}
              imageReferences = {imageReferences}
              tokenSelections = {tokenSelections}
              setSubscriptions= {setSubscriptions}
            />
          </div>

          {/* Body */}
          <div>
            {pendingBlock}

            {unclaimedUSD > 0 && enableClaiming && (
              isClaiming ? (
                <Progress indeterminate style={{ marginTop: 10 }} />
              ) : (
                <Button
                  rounded
                  severity="warning"
                  size="small"
                  disabled={isClaiming}
                  onClick={handleClaim}
                  style={{
                    marginTop : 10,
                    width     : 200,
                    justifyContent: 'center'
                  }}
                >
                  Claim
                </Button>
              )
            )}
          </div>
        </div>
      </div>
    );
  }
);

export default ROCKETFIRewards;


// import React, { useEffect, useState, useCallback } from "react";
// import BigNumber from "bignumber.js";
// import { Button } from 'primereact/button';
// import { Progress } from '@nextui-org/react';
// import { formatCurrencyExtended, getTokenPrice } from '../../utils';
// import RewardPackProfile from './RewardPackProfile';

// // The "ROCKETFIRewards" component calculates + claims unclaimed reward pack dividends
// const ROCKETFIRewards = React.memo(({
//   account,
//   contracts,
//   imageReferences,
//   tokenSelections,
//   viewTotalDistributed = false,
//   enableClaiming = false,
//   refreshData
// }) => {
//   const [unclaimedRewardsUSD, setUnclaimedRewardsUSD] = useState(0);
//   const [isClaiming, setIsClaiming] = useState(false);

//   // ADDED: "isLoadingRewards" to track whether we are fetching the reward amounts
//   const [isLoadingRewards, setIsLoadingRewards] = useState(false);

//   // subscriptions is populated by the <RewardPackProfile />
//   const [subscriptions, setSubscriptions] = useState([]);

//   // Define the async function to fetch unclaimed rewards
//   const calculateUnclaimedRewardsUSD = useCallback(async () => {
//     if (!account || !contracts?.rp_rewardPacks || subscriptions.length === 0) {
//       setUnclaimedRewardsUSD(0);
//       return;
//     }

//     try {
//       setIsLoadingRewards(true);
//       let totalUnclaimed = new BigNumber(0);

//       for (const subscriptionAddress of subscriptions) {
//         const tokenInfo = tokenSelections.find(
//           (t) => t?.address?.toLowerCase() === subscriptionAddress?.toLowerCase()
//         );
//         if (!tokenInfo) continue;

//         // Get the unclaimed token amount for this subscription
//         const unclaimedAmount = await contracts.rp_rewardPacks.methods
//           .getTotalUnrealisedWithRealisedUndeposited(account, subscriptionAddress)
//           .call();

//         // Convert to USD
//         const tokenPrice = await getTokenPrice(tokenSelections, tokenInfo.label);
//         if (!tokenPrice) continue; // fallback if price is not found
//         const valueUSD = new BigNumber(unclaimedAmount)
//           .times(tokenPrice)
//           .div(new BigNumber(10).pow(tokenInfo.decimals));

//         totalUnclaimed = totalUnclaimed.plus(valueUSD);
//       }

//       setUnclaimedRewardsUSD(totalUnclaimed.toNumber());
//     } catch (error) {
//       console.error("Error calculating RocketFi unclaimed rewards:", error);
//       // Optionally set unclaimedRewardsUSD to 0 or some fallback
//       setUnclaimedRewardsUSD(0);
//     } finally {
//       setIsLoadingRewards(false);
//     }
//   }, [account, contracts?.rp_rewardPacks, subscriptions, tokenSelections]);

//   // UseEffect that calls our "calculateUnclaimedRewardsUSD" whenever dependencies change
//   useEffect(() => {
//     calculateUnclaimedRewardsUSD();
//   }, [calculateUnclaimedRewardsUSD]);

//   const handleClaim = async () => {
//     setIsClaiming(true);

//     if (!contracts?.rp_rewardPacks) {
//       console.error("Reward Pack contract not available.");
//       setIsClaiming(false);
//       return;
//     }

//     console.log("Reward Pack: Claim Rewards button clicked");

//     try {
//       const tx = contracts.rp_rewardPacks.methods
//         .claimAllCurrentDividends()
//         .send({ from: account });

//       tx.on("transactionHash", (hash) => {
//         console.log(
//           `Transaction Hash: ${hash}. Follow the transaction on BscScan: https://bscscan.com/tx/${hash}`
//         );
//       });

//       await tx;

//       // Recalculate unclaimed rewards
//       await calculateUnclaimedRewardsUSD();

//       // Also call parent's refreshData if provided
//       if (refreshData) {
//         refreshData();
//       }
//     } catch (error) {
//       console.error("Error claiming rewards in Reward Pack:", error);
//     }

//     setIsClaiming(false);
//   };

//   // Decide what to display for the "Pending" section
//   let pendingSection = null;
//   if (isLoadingRewards) {
//     // Show a small spinner or "Loading..." while calculating
//     pendingSection = <div>Loading Rewards...</div>;
//   } else {
//     pendingSection = (
//       <div>
//         Pending: <strong>{formatCurrencyExtended(unclaimedRewardsUSD)}</strong>
//       </div>
//     );
//   }

//   return (
//     <div
//       style={{
//         display: 'flex',
//         justifyContent: 'flex-start',
//         alignItems: 'center',
//         borderRadius: '10px',
//         backgroundColor: 'rgba(50, 100, 255, 0.05)',
//         padding: '5px 15px',
//         fontSize: '11px',
//       }}
//     >
//       <div>
//         {/* Header */}
//         <div
//           style={{
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center',
//             flexDirection: 'column',
//             marginBottom: '8px'
//           }}
//         >
//           <div style={{ fontSize: '12px' }}>
//           <b> Reward Pack</b> 
//           </div>

//           {/* RewardPackProfile handles the subscription logic, letting user pick tokens */}
//           <RewardPackProfile
//             holderAddress={account}
//             imageReferences={imageReferences}
//             tokenSelections={tokenSelections}
//             setSubscriptions={setSubscriptions}
//           />
//         </div>

//         {/* Body */}
//        { //unclaimedRewardsUSD > 0 && 
//         <div style={{ fontSize: '11px' }}>
//           {isClaiming ? (
//             <Progress indeterminate color="gradient" status="secondary" size="xl" />
//           ) : (
//             <>
//               {pendingSection}

//               {unclaimedRewardsUSD > 0 && 
//               enableClaiming && (
//                 <Button
//                   rounded
//                   severity="warning"
//                   onClick={handleClaim}
//                   size="small"
//                   style={{
//                     marginTop: '10px',
//                     alignSelf: 'center',
//                     width: '200px',
//                     justifyContent: 'center',
//                   }}
//                   disabled={isClaiming}
//                 >
//                   Claim
//                 </Button>
//               )}
//             </>
//           )}
//         </div>}
//       </div>
//     </div>
//   );
// });

// export default ROCKETFIRewards;
